import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { 
  X, 
  Truck, 
  User, 
  MapPin, 
  Clock, 
  Package,
  AlertCircle,
  Calendar,
  Users,
  Route
} from 'lucide-react'
import { apiClient } from '../config/api'
import AlertRuleEditor from './AlertRuleEditor'
import { useSettings } from '../contexts/SettingsContext'

interface Staff {
  id: number
  staff_code: string
  full_name: string
  driver_license_class: string
  employment_status: string
  performance_rating: number
  staff_type: string
  department: string
  position: string
  company_name: string
}

interface Vehicle {
  id: number
  name: string
  plateNumber: string
  identifierType: string
  status: string
  vehicleId: string
  containerInfo?: any
  assetInfo?: any
  customerInfo?: any
}

interface Customer {
  id: number
  company_name: string
  customer_code: string
  contact_person: string
  contact_phone: string
}

interface MissionCreatorProps {
  isOpen: boolean
  onClose: () => void
  onMissionCreated: (mission: any) => void
  editingMission?: any | null
  showMessage?: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

const MissionCreator: React.FC<MissionCreatorProps> = ({ 
  isOpen, 
  onClose, 
  onMissionCreated, 
  editingMission = null,
  showMessage 
}) => {
  const { t } = useTranslation()
  const { get: getSetting } = useSettings()
  const configuredPriority = String(getSetting('missions.default_priority', 'MEDIUM'))
  const defaultPriority = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'].includes(configuredPriority)
    ? configuredPriority
    : 'MEDIUM'
  const defaultDurationMinutes = Math.max(15, Number(getSetting('missions.default_duration_minutes', 480)))
  const [loading, setLoading] = useState(false)
  const [staff, setStaff] = useState<Staff[]>([])
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [geofences, setGeofences] = useState<any[]>([])
  const [corridors, setCorridors] = useState<any[]>([])
  
  const [formData, setFormData] = useState({
    mission_name: '',
    mission_type: 'DELIVERY',
    priority: defaultPriority,
    assigned_staff_ids: [], // Multiple staff assignment
    primary_staff_id: '', // Primary responsible staff
    assigned_vehicle_ids: [], // Multiple vehicle assignment
    customer_id: '',
    planned_start_time: '',
    planned_end_time: '',
    description: '',
    selected_alerts: [], // Selected alerts for mission monitoring
    origin_address: '', // Manual origin address
    destination_address: '' // Manual destination address
  })

  const [selectedStaff, setSelectedStaff] = useState<Staff[]>([]) // Track selected staff
  const [selectedVehicles, setSelectedVehicles] = useState<Vehicle[]>([]) // Track selected vehicles
  const [selectedGeofenceIds, setSelectedGeofenceIds] = useState<string[]>([]) // Mission zones
  const [selectedCorridorIds, setSelectedCorridorIds] = useState<string[]>([]) // Mission corridors
  const [vehicleFilter, setVehicleFilter] = useState({
    customer: '',
    type: '',
    search: ''
  })
  const [showAlertCreation, setShowAlertCreation] = useState(false)

  // Get staff types required for each mission type
  const getRequiredStaffTypes = (missionType: string): string[] => {
    switch (missionType) {
      // Transport Operations
      case 'DELIVERY':
      case 'PICKUP':
      case 'ROUND_TRIP':
      case 'MULTI_STOP':
        return ['DRIVER']
      
      // Logistics Operations
      case 'LOADING':
      case 'UNLOADING':
        return ['DRIVER', 'LOADER']
      case 'CUSTOMS':
        return ['DRIVER', 'COORDINATOR']
      case 'INSPECTION':
        return ['DRIVER', 'SUPERVISOR']
      
      // Maintenance Operations
      case 'MAINTENANCE':
      case 'REPAIR':
        return ['DRIVER', 'MAINTENANCE']
      case 'REFUELING':
        return ['DRIVER']
      
      // Security Operations
      case 'SECURITY_ESCORT':
      case 'BORDER_CROSSING':
      case 'CHECKPOINT':
        return ['DRIVER', 'SECURITY']
      
      // Provider Operations (NEW)
      case 'FREIGHT_FORWARDING':
      case 'CUSTOMS_BROKERAGE':
        return ['COORDINATOR']
      case 'TRANSPORT_CONTRACT':
        return ['DRIVER', 'COORDINATOR']
      case 'LOGISTICS_SERVICE':
        return ['COORDINATOR', 'LOADER']
      case 'WAREHOUSE_OPERATION':
        return ['LOADER', 'SUPERVISOR']
      case 'INSURANCE_CLAIM':
        return ['COORDINATOR', 'SUPERVISOR']
      
      // Administrative Operations
      case 'DOCUMENTATION':
        return ['COORDINATOR']
      case 'COMPLIANCE_CHECK':
        return ['SUPERVISOR']
      case 'TRAINING':
        return ['SUPERVISOR']
      
      default:
        return ['DRIVER']
    }
  }

  // Filter staff based on mission type (allow multi-role staff)
  const getAvailableStaff = () => {
    const requiredTypes = getRequiredStaffTypes(formData.mission_type)
    
    // A staff member can fulfill multiple roles
    // For example, a DRIVER can also act as COORDINATOR for some missions
    return Array.isArray(staff) ? staff.filter(s => {
      // Mission customer selected: only offer that customer's staff (or shared staff)
      const staffCustomerId = (s as any).customer_id
      if (selectedCustomer && staffCustomerId != null &&
          String(staffCustomerId) !== String(selectedCustomer.id)) return false
      
      // Always include if exact match
      if (requiredTypes.includes(s.staff_type)) return true
      
      // Allow cross-role assignments based on experience/qualifications
      if (requiredTypes.includes('COORDINATOR') && s.staff_type === 'DRIVER' && s.total_missions_completed > 50) return true
      if (requiredTypes.includes('SUPERVISOR') && s.staff_type === 'DRIVER' && s.total_missions_completed > 100) return true
      if (requiredTypes.includes('LOADER') && s.staff_type === 'DRIVER') return true
      
      return false
    }) : []
  }

  // Selected mission customer (drives customer-consistent pickers)
  const selectedCustomer = Array.isArray(customers)
    ? customers.find(c => c.id.toString() === formData.customer_id)
    : undefined

  // Filter vehicles based on filters
  const getFilteredVehicles = () => {
    if (!Array.isArray(vehicles)) return []
    
    return vehicles.filter(vehicle => {
      // Mission customer selected: only offer that customer's vehicles
      if (selectedCustomer && vehicle.customer_name !== selectedCustomer.company_name) return false
      
      // Filter by customer
      if (vehicleFilter.customer && vehicle.customer_name !== vehicleFilter.customer) return false
      
      // Filter by type
      if (vehicleFilter.type && (vehicle.vehicle_source || vehicle.vehicle_type) !== vehicleFilter.type) return false
      
      // Filter by search term
      if (vehicleFilter.search) {
        const searchTerm = vehicleFilter.search.toLowerCase()
        const vehicleName = (vehicle.name || vehicle.vehicle_code || '').toLowerCase()
        const licensePlate = (vehicle.license_plate || '').toLowerCase()
        const customerName = (vehicle.customer_name || '').toLowerCase()
        
        if (!vehicleName.includes(searchTerm) && 
            !licensePlate.includes(searchTerm) && 
            !customerName.includes(searchTerm)) {
          return false
        }
      }
      
      return true
    })
  }

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadFormData()
    }
  }, [isOpen])

  // Populate form when editing mission AND data is loaded
  useEffect(() => {
    if (isOpen && editingMission && !loading && staff.length > 0) {
      console.log('🔄 Populating form with mission data (data loaded):', editingMission)
      console.log('📝 Mission details:', {
        name: editingMission.mission_name,
        type: editingMission.mission_type,
        priority: editingMission.priority,
        staff_id: editingMission.assigned_staff_id,
        customer_id: editingMission.customer_id,
        start_time: editingMission.planned_start_time,
        end_time: editingMission.planned_end_time
      })
      console.log('📊 Available staff:', staff.length, 'customers:', customers.length)
      
      // Format dates for datetime-local input
      const formatDateTime = (dateString: string) => {
        if (!dateString) return ''
        try {
          const date = new Date(dateString)
          const formatted = date.toISOString().slice(0, 16)
          console.log(`📅 Formatted date: ${dateString} → ${formatted}`)
          return formatted
        } catch (error) {
          console.error('Date formatting error:', error)
          return ''
        }
      }

      // Verify staff and customer exist in loaded data
      const staffExists = staff.find(s => s.id.toString() === editingMission.assigned_staff_id?.toString())
      const customerExists = customers.find(c => c.id.toString() === editingMission.customer_id?.toString())
      
      console.log('🔍 Staff verification:', {
        looking_for: editingMission.assigned_staff_id,
        found: staffExists ? `${staffExists.full_name} (${staffExists.staff_type})` : 'NOT FOUND',
        available_staff: staff.slice(0, 3).map(s => `${s.id}: ${s.full_name}`)
      })
      
      console.log('🔍 Customer verification:', {
        looking_for: editingMission.customer_id,
        found: customerExists ? customerExists.company_name : 'NOT FOUND',
        available_customers: customers.map(c => `${c.id}: ${c.company_name}`)
      })

      const newFormData = {
        mission_name: editingMission.mission_name || '',
        mission_type: editingMission.mission_type || 'DELIVERY',
        priority: editingMission.priority || 'MEDIUM',
        assigned_staff_ids: [],
        primary_staff_id: editingMission.assigned_staff_id?.toString() || '',
        assigned_vehicle_ids: [],
        customer_id: editingMission.customer_id?.toString() || '',
        planned_start_time: formatDateTime(editingMission.planned_start_time),
        planned_end_time: formatDateTime(editingMission.planned_end_time),
        description: editingMission.description || '',
        selected_alerts: [],
        origin_address: editingMission.origin_address || '',
        destination_address: editingMission.destination_address || ''
      }
      
      console.log('📝 Setting form data:', newFormData)
      setFormData(newFormData)
      
      // Restore linked vehicles/zones saved with the mission
      const editVehicleIds: any[] = Array.isArray(editingMission.assigned_vehicle_ids) ? editingMission.assigned_vehicle_ids : []
      setSelectedVehicles(
        Array.isArray(vehicles)
          ? vehicles.filter(v => editVehicleIds.some(id => String(id) === String(v.vehicle_id || v.id)))
          : []
      )
      setSelectedGeofenceIds(Array.isArray(editingMission.geofence_ids) ? editingMission.geofence_ids.filter(Boolean) : [])
      setSelectedCorridorIds(Array.isArray(editingMission.corridor_ids) ? editingMission.corridor_ids.filter(Boolean) : [])
      console.log('✅ Form populated with mission data')
    } else if (isOpen && !editingMission && !loading) {
      // Reset form for new mission (only when data is loaded)
      console.log('🆕 Resetting form for new mission')
      setFormData({
        mission_name: '',
        mission_type: 'DELIVERY',
        priority: defaultPriority,
        assigned_staff_ids: [],
        primary_staff_id: '',
        assigned_vehicle_ids: [],
        customer_id: '',
        planned_start_time: '',
        planned_end_time: '',
        description: '',
        selected_alerts: [],
        origin_address: '',
        destination_address: ''
      })
    }
  }, [isOpen, editingMission, loading, staff, customers, defaultPriority])

  // Debug effect to track form data changes
  useEffect(() => {
    if (isOpen) {
      console.log('📝 Form data changed:', {
        mission_name: formData.mission_name,
        mission_type: formData.mission_type,
        priority: formData.priority,
        primary_staff_id: formData.primary_staff_id,
        customer_id: formData.customer_id
      })
    }
  }, [formData, isOpen])

  const loadFormData = async () => {
    try {
      setLoading(true)
      console.log('🔄 Loading form data for mission creator...')

      // Check authentication first
      const token = localStorage.getItem('fleet_access_token')
      if (!token) {
        console.error('❌ No authentication token found')
        showMessage?.('Please login first to create missions', 'error')
        setLoading(false)
        return
      }
      console.log('✅ Authentication token found')

      // Load all active staff (not just drivers)
      console.log('📊 Loading staff from /api/staff...')
      try {
        const staffResponse = await apiClient.get('/staff?employment_status=ACTIVE')
        console.log('📊 Raw staff response:', staffResponse)
        
        // apiClient returns { data, status }, so we need staffResponse.data
        const staffData = staffResponse.data || staffResponse
        const staffArray = Array.isArray(staffData) ? staffData : []
        setStaff(staffArray)
        console.log('✅ Loaded staff:', staffArray.length)
        
        if (staffArray.length > 0) {
          console.log('📋 Staff types available:', [...new Set(staffArray.map(s => s.staff_type))])
          console.log('📋 First 3 staff:', staffArray.slice(0, 3).map(s => `${s.full_name} (${s.staff_type})`))
        } else {
          console.warn('⚠️ No staff data returned from API')
        }
      } catch (staffError) {
        console.error('❌ Failed to load staff:', staffError)
        setStaff([])
        showMessage?.(`Failed to load staff data: ${staffError.message}`, 'error')
      }

      // Load available vehicles
      console.log('🚛 Loading vehicles from /api/vehicles...')
      try {
        const vehiclesResponse = await apiClient.get('/vehicles')
        console.log('🚛 Raw vehicles response:', vehiclesResponse)
        
        // apiClient returns { data, status }, so we need vehiclesResponse.data
        const vehiclesData = vehiclesResponse.data || vehiclesResponse
        const vehiclesArray = Array.isArray(vehiclesData) ? vehiclesData : []
        setVehicles(vehiclesArray)
        console.log('✅ Loaded vehicles:', vehiclesArray.length)
        
        if (vehiclesArray.length > 0) {
          console.log('📋 First 3 vehicles:', vehiclesArray.slice(0, 3).map(v => v.name || v.plateNumber || v.id))
          console.log('📋 Vehicle types:', [...new Set(vehiclesArray.map(v => v.identifierType || 'Unknown'))])
        }
      } catch (vehicleError) {
        console.error('❌ Failed to load vehicles:', vehicleError)
        setVehicles([])
        showMessage?.('Failed to load vehicle data', 'error')
      }

      // Load customers
      console.log('🏢 Loading customers from /api/customers...')
      try {
        const customersResponse = await apiClient.get('/customers')
        console.log('🏢 Raw customers response:', customersResponse)
        
        // apiClient returns { data, status }, so we need customersResponse.data
        const customersData = customersResponse.data || customersResponse
        const customersArray = Array.isArray(customersData) ? customersData : []
        setCustomers(customersArray)
        console.log('✅ Loaded customers:', customersArray.length)
      } catch (customerError) {
        console.log('⚠️ Customers endpoint not available:', customerError.message)
        setCustomers([])
      }

      // Load geofences
      console.log('📍 Loading geofences from /api/geofences...')
      try {
        const geofencesResponse = await apiClient.get('/geofences')
        console.log('📍 Raw geofences response:', geofencesResponse)
        
        const geofencesData = geofencesResponse.data || geofencesResponse
        const geofencesArray = Array.isArray(geofencesData) ? geofencesData : []
        setGeofences(geofencesArray)
        console.log('✅ Loaded geofences:', geofencesArray.length)
      } catch (geofenceError) {
        console.log('⚠️ Geofences endpoint not available:', geofenceError.message)
        setGeofences([])
      }

      // Load corridors
      console.log('🛣️ Loading corridors from /api/corridors...')
      try {
        const corridorsResponse = await apiClient.get('/corridors')
        console.log('🛣️ Raw corridors response:', corridorsResponse)
        
        const corridorsData = corridorsResponse.data || corridorsResponse
        const corridorsArray = Array.isArray(corridorsData) ? corridorsData : []
        setCorridors(corridorsArray)
        console.log('✅ Loaded corridors:', corridorsArray.length)
      } catch (corridorError) {
        console.log('⚠️ Corridors endpoint not available:', corridorError.message)
        setCorridors([])
      }

    } catch (error) {
      console.error('❌ Failed to load form data:', error)
      showMessage?.('Failed to load form data', 'error')
      
      // Ensure arrays are initialized even on error
      setStaff([])
      setVehicles([])
      setCustomers([])
      setGeofences([])
      setCorridors([])
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.mission_name || !formData.primary_staff_id) {
      showMessage?.('Please fill in mission name and assign primary staff', 'warning')
      return
    }

    try {
      setLoading(true)
      const isEditing = !!editingMission
      console.log(isEditing ? `🔄 Updating mission: ${editingMission.id}` : '🎯 Creating new mission with multiple staff...')

      // Find primary staff member
      const primaryStaff = Array.isArray(staff) ? staff.find(s => s.id.toString() === formData.primary_staff_id) : null
      
      // Prepare staff assignments
      const allAssignedStaff = [
        { id: formData.primary_staff_id, role: 'PRIMARY', staff_info: primaryStaff },
        ...selectedStaff.map(s => ({ id: s.id, role: 'SUPPORT', staff_info: s }))
      ]
      
      const missionData = {
        ...formData,
        assigned_staff_id: formData.primary_staff_id, // Primary staff for backward compatibility
        assigned_driver: primaryStaff?.full_name || 'Unknown Staff',
        driver_license_required: primaryStaff?.driver_license_class,
        customer_name: Array.isArray(customers) ? customers.find(c => c.id.toString() === formData.customer_id)?.company_name || 'Manual Entry' : 'Manual Entry',
        // Legacy single-vehicle field: first picked vehicle (kept for backward compatibility)
        assigned_vehicle_id: selectedVehicles.length > 0 ? parseInt(String(selectedVehicles[0].vehicle_id ?? selectedVehicles[0].id)) : null,
        customer_id: formData.customer_id ? parseInt(formData.customer_id) : null,
        
        // Fix timestamp fields - convert empty strings to null
        planned_start_time: formData.planned_start_time || null,
        planned_end_time: formData.planned_end_time || null,
        
        // Multiple staff assignment data
        staff_assignments: allAssignedStaff,
        total_staff_assigned: allAssignedStaff.length,
        
        // Vehicles selected in the picker (was previously never sent — assignments were lost)
        assigned_vehicle_ids: selectedVehicles.map(v => v.vehicle_id || v.id),
        
        // Mission zones (geofences/corridors linked to the mission)
        geofence_ids: selectedGeofenceIds,
        corridor_ids: selectedCorridorIds
      }
      
      console.log('📊 Mission will be assigned to:', allAssignedStaff.length, 'staff members')
      console.log('   Primary:', primaryStaff?.full_name, '(' + primaryStaff?.staff_type + ')')
      if (selectedStaff.length > 0) {
        console.log('   Additional:', selectedStaff.map(s => s.full_name + ' (' + s.staff_type + ')').join(', '))
      }

      // Use PUT for editing, POST for creating
      const response = isEditing 
        ? await apiClient.put(`/missions/${editingMission.id}`, missionData)
        : await apiClient.post('/missions', missionData)
      
      console.log(isEditing ? '✅ Mission updated:' : '✅ Mission created:', response)

      showMessage?.(`Mission "${formData.mission_name}" created successfully`, 'success')
      onMissionCreated(response) // Pass full response
      onClose()
      
      // Reset form
      setFormData({
        mission_name: '',
        mission_type: 'DELIVERY',
        priority: defaultPriority,
        assigned_staff_ids: [],
        primary_staff_id: '',
        assigned_vehicle_ids: [],
        customer_id: '',
        planned_start_time: '',
        planned_end_time: '',
        description: '',
        selected_alerts: [],
        origin_address: '',
        destination_address: ''
      })
      setSelectedStaff([])
      setSelectedVehicles([])
      setSelectedGeofenceIds([])
      setSelectedCorridorIds([])
      setVehicleFilter({ customer: '', type: '', search: '' })

    } catch (error) {
      console.error('❌ Failed to create mission:', error)
      showMessage?.('Failed to create mission', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-30" />
      
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-6xl max-h-[85vh] h-[85vh] flex flex-col">
        {/* Mac-style Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-b from-slate-700 to-slate-900 border-b border-slate-600 rounded-t-lg">
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="group w-3 h-3 bg-gradient-to-b from-red-400 to-red-500 rounded-full hover:from-red-500 hover:to-red-600 transition-all duration-200 shadow-sm border border-red-600/20 relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
            <button className="group w-3 h-3 bg-gradient-to-b from-yellow-400 to-yellow-500 rounded-full hover:from-yellow-500 hover:to-yellow-600 transition-all duration-200 shadow-sm border border-yellow-600/20 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
            <button className="group w-3 h-3 bg-gradient-to-b from-green-400 to-green-500 rounded-full hover:from-green-500 hover:to-green-600 transition-all duration-200 shadow-sm border border-green-600/20 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </div>
          
          <div className="flex items-center space-x-3">
            <Package className="w-5 h-5 text-white" />
            <h2 className="text-white font-medium">
              {editingMission ? t('edit_mission_title') : t('create_new_mission')}
            </h2>
          </div>
          
          <div className="w-12"></div> {/* Spacer for centering */}
        </div>
        
        {/* Progress Bar */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              {editingMission ? 'Mission Editing' : 'Mission Setup Progress'}
            </span>
            <span className="text-sm text-gray-500">
              {editingMission ? 'Update mission details' : 'Step 1 of 1'}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: '100%' }} />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading staff and vehicle data...</span>
            </div>
          )}


          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Mission Basic Info */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Truck className="w-5 h-5 mr-2" />
                {t('mission_information')}
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('mission_name')} *
                  </label>
                  <input
                    type="text"
                    value={formData.mission_name}
                    onChange={(e) => handleInputChange('mission_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Delivery to Port of Bamako"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('mission_type')}
                  </label>
                  <select
                    value={formData.mission_type}
                    onChange={(e) => handleInputChange('mission_type', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <optgroup label="Transport Operations">
                      <option value="DELIVERY">Delivery</option>
                      <option value="PICKUP">Pickup</option>
                      <option value="ROUND_TRIP">Round Trip</option>
                      <option value="MULTI_STOP">Multi-Stop Route</option>
                    </optgroup>
                    <optgroup label="Logistics Operations">
                      <option value="LOADING">Loading Operation</option>
                      <option value="UNLOADING">Unloading Operation</option>
                      <option value="CUSTOMS">Customs Clearance</option>
                      <option value="INSPECTION">Vehicle Inspection</option>
                    </optgroup>
                    <optgroup label="Maintenance Operations">
                      <option value="MAINTENANCE">Vehicle Maintenance</option>
                      <option value="REPAIR">Emergency Repair</option>
                      <option value="REFUELING">Refueling Operation</option>
                    </optgroup>
                    <optgroup label="Security Operations">
                      <option value="SECURITY_ESCORT">Security Escort</option>
                      <option value="BORDER_CROSSING">Border Crossing</option>
                      <option value="CHECKPOINT">Security Checkpoint</option>
                    </optgroup>
                    <optgroup label="Provider Operations">
                      <option value="FREIGHT_FORWARDING">Freight Forwarding (Transitaire)</option>
                      <option value="CUSTOMS_BROKERAGE">Customs Brokerage (Courtier en Douane)</option>
                      <option value="TRANSPORT_CONTRACT">Transport Contract</option>
                      <option value="LOGISTICS_SERVICE">Logistics Service</option>
                      <option value="WAREHOUSE_OPERATION">Warehouse Operation</option>
                      <option value="INSURANCE_CLAIM">Insurance Claim</option>
                    </optgroup>
                    <optgroup label="Administrative Operations">
                      <option value="DOCUMENTATION">Documentation</option>
                      <option value="COMPLIANCE_CHECK">Compliance Check</option>
                      <option value="TRAINING">Staff Training</option>
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('priority')}
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="URGENT">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('description')}
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Mission details..."
                  />
                </div>
              </div>
            </div>

            {/* Staff Assignment */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                <User className="w-4 h-4 mr-2" />
                {t('staff_assignment')}
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Assign Staff Members * 
                  <span className="text-xs text-blue-600 ml-2">
                    (Required: {getRequiredStaffTypes(formData.mission_type).join(', ')})
                  </span>
                </label>
                
                {/* Primary Staff Selection */}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Primary Responsible Staff:</label>
                  <select
                    value={formData.primary_staff_id}
                    onChange={(e) => {
                      const newPrimaryId = e.target.value
                      handleInputChange('primary_staff_id', newPrimaryId)
                      
                      // Automatically add primary staff to selected staff if not already there
                      if (newPrimaryId) {
                        const primaryStaffMember = getAvailableStaff().find(s => s.id.toString() === newPrimaryId)
                        if (primaryStaffMember && !selectedStaff.some(s => s.id === primaryStaffMember.id)) {
                          setSelectedStaff([...selectedStaff, primaryStaffMember])
                        }
                      } else {
                        // If primary staff is deselected, remove them from additional staff too
                        const oldPrimaryId = formData.primary_staff_id
                        if (oldPrimaryId) {
                          setSelectedStaff(selectedStaff.filter(s => s.id.toString() !== oldPrimaryId))
                        }
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select primary staff...</option>
                    {getAvailableStaff().map(staffMember => (
                      <option key={staffMember.id} value={staffMember.id}>
                        {staffMember.full_name} - {staffMember.position || staffMember.staff_type} 
                        {staffMember.department && ` (${staffMember.department})`}
                        {staffMember.driver_license_class && ` - ${staffMember.driver_license_class}`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Additional Staff Selection */}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Additional Staff (Optional):</label>
                  <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-2">
                    {getAvailableStaff().map(staffMember => {
                      const isPrimary = formData.primary_staff_id === staffMember.id.toString()
                      const isSelected = selectedStaff.some(s => s.id === staffMember.id)
                      
                      return (
                        <label key={staffMember.id} className={`flex items-center space-x-2 text-sm ${isPrimary ? 'bg-blue-50 border border-blue-200 rounded p-1' : ''}`}>
                          <input
                            type="checkbox"
                            checked={isSelected || isPrimary}
                            disabled={isPrimary} // Primary staff is automatically selected
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedStaff([...selectedStaff, staffMember])
                              } else {
                                setSelectedStaff(selectedStaff.filter(s => s.id !== staffMember.id))
                              }
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                          />
                          <span className="flex-1">
                            {staffMember.full_name} - {staffMember.position || staffMember.staff_type}
                            {staffMember.department && ` (${staffMember.department})`}
                            {isPrimary && <span className="text-blue-600 font-medium"> (PRIMARY)</span>}
                            
                            {/* Show what roles this staff can fulfill */}
                            <div className="text-xs text-gray-500 mt-1">
                              Can work as: {staffMember.staff_type}
                              {staffMember.staff_type === 'DRIVER' && staffMember.total_missions_completed > 50 && ', COORDINATOR'}
                              {staffMember.staff_type === 'DRIVER' && staffMember.total_missions_completed > 100 && ', SUPERVISOR'}
                              {staffMember.staff_type === 'DRIVER' && ', LOADER'}
                            </div>
                          </span>
                        </label>
                      )
                    })}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Total staff assigned: {(formData.primary_staff_id ? 1 : 0) + selectedStaff.length}
                    {formData.primary_staff_id && ` (1 primary + ${selectedStaff.length} additional)`}
                  </p>
                </div>

                {getAvailableStaff().length === 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    {loading ? 'Loading available staff...' : `No ${getRequiredStaffTypes(formData.mission_type).join('/')} staff available for this mission type`}
                  </p>
                )}
                
                {/* Show mission type requirements */}
                <div className="mt-2 p-2 bg-blue-50 rounded">
                  <p className="text-xs text-blue-800">
                    <strong>Required:</strong> {getRequiredStaffTypes(formData.mission_type).join(', ')}
                  </p>
                  <div className="text-xs text-blue-700 mt-1">
                    {getRequiredStaffTypes(formData.mission_type).map(type => {
                      const directCount = staff.filter(s => s.staff_type === type).length
                      const crossRoleCount = type === 'COORDINATOR' ? 
                        staff.filter(s => s.staff_type === 'DRIVER' && s.total_missions_completed > 50).length : 0
                      const totalCount = directCount + crossRoleCount
                      
                      return (
                        <span key={type} className="mr-3">
                          {type}: {totalCount} 
                          {crossRoleCount > 0 && ` (${directCount} + ${crossRoleCount} cross-role)`}
                        </span>
                      )
                    })}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    Note: Experienced drivers (50+ missions) can act as coordinators
                  </div>
                </div>
              </div>
            </div>

            {/* Vehicle Assignment */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Truck className="w-5 h-5 mr-2" />
                {t('vehicle_assignment')}
              </h3>
              

              {/* Vehicle Filters */}
              <div className="mb-4 grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Filter by Customer:</label>
                  <select
                    value={vehicleFilter.customer}
                    onChange={(e) => setVehicleFilter({...vehicleFilter, customer: e.target.value})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Customers ({Array.isArray(vehicles) ? vehicles.length : 0})</option>
                    {Array.isArray(vehicles) && (() => {
                      const customerCounts = {}
                      vehicles.forEach(v => {
                        if (v.customer_name) {
                          customerCounts[v.customer_name] = (customerCounts[v.customer_name] || 0) + 1
                        }
                      })
                      
                      return Object.entries(customerCounts)
                        .sort(([,a], [,b]) => b - a)
                        .map(([customer, count]) => (
                          <option key={customer} value={customer}>
                            {customer} ({count} vehicles)
                          </option>
                        ))
                    })()}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Filter by Type:</label>
                  <select
                    value={vehicleFilter.type}
                    onChange={(e) => setVehicleFilter({...vehicleFilter, type: e.target.value})}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Types ({Array.isArray(vehicles) ? vehicles.length : 0})</option>
                    {Array.isArray(vehicles) && (() => {
                      const typeCounts = {}
                      vehicles.forEach(v => {
                        const type = v.vehicle_source || v.vehicle_type || 'Unknown'
                        typeCounts[type] = (typeCounts[type] || 0) + 1
                      })
                      
                      return Object.entries(typeCounts).map(([type, count]) => (
                        <option key={type} value={type}>
                          {type} ({count} vehicles)
                        </option>
                      ))
                    })()}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Search:</label>
                  <input
                    type="text"
                    value={vehicleFilter.search}
                    onChange={(e) => setVehicleFilter({...vehicleFilter, search: e.target.value})}
                    placeholder="Search vehicles..."
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Vehicle Selection */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Vehicles (Optional)
                    <span className="text-xs text-blue-600 ml-2">
                      ({getFilteredVehicles().length} vehicles match filters)
                    </span>
                  </label>
                  
                  {/* Select All / Clear All Buttons */}
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        const filteredVehicles = getFilteredVehicles()
                        const newSelected = [...selectedVehicles]
                        
                        filteredVehicles.forEach(vehicle => {
                          const vehicleId = vehicle.vehicle_id || vehicle.id
                          if (!newSelected.some(v => (v.vehicle_id || v.id) === vehicleId)) {
                            newSelected.push(vehicle)
                          }
                        })
                        
                        setSelectedVehicles(newSelected)
                      }}
                      className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                    >
                      {t('select_all')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedVehicles([])}
                      className="px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                    >
                      {t('clear_all')}
                    </button>
                  </div>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {getFilteredVehicles().map(vehicle => {
                    const vehicleId = vehicle.vehicle_id || vehicle.id
                    const isSelected = selectedVehicles.some(v => (v.vehicle_id || v.id) === vehicleId)
                    
                    let vehicleName = vehicle.name || vehicle.vehicle_code || `Vehicle ${vehicleId}`
                    let vehicleInfo = []
                    
                    // For assets
                    if (vehicle.vehicle_source === 'ASSET') {
                      if (vehicle.make && vehicle.model) vehicleName = `${vehicle.make} ${vehicle.model}`
                      if (vehicle.vehicle_code) vehicleInfo.push(`Asset: ${vehicle.vehicle_code}`)
                      if (vehicle.license_plate && vehicle.license_plate !== 'N/A') vehicleInfo.push(`Plate: ${vehicle.license_plate}`)
                    }
                    
                    // For containers
                    if (vehicle.vehicle_source === 'CONTAINER') {
                      vehicleName = vehicle.vehicle_code || 'Container'
                      if (vehicle.model && vehicle.model !== 'N/A') vehicleInfo.push(`Type: ${vehicle.model}`)
                      if (vehicle.make && vehicle.make !== 'N/A') vehicleInfo.push(`Owner: ${vehicle.make}`)
                    }
                    
                    if (vehicle.customer_name) vehicleInfo.push(`Customer: ${vehicle.customer_name}`)
                    
                    return (
                      <label key={vehicleId} className="flex items-center space-x-2 text-sm hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedVehicles([...selectedVehicles, vehicle])
                            } else {
                              setSelectedVehicles(selectedVehicles.filter(v => (v.vehicle_id || v.id) !== vehicleId))
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="flex-1">
                          {vehicleName}
                          {vehicleInfo.length > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                              {vehicleInfo.join(' • ')}
                            </div>
                          )}
                        </span>
                      </label>
                    )
                  })}
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  {selectedVehicles.length} vehicles selected
                  {getFilteredVehicles().length !== vehicles.length && ` (${getFilteredVehicles().length} shown after filters)`}
                </p>
              </div>
                
                {(!Array.isArray(vehicles) || vehicles.length === 0) && (
                  <p className="text-sm text-gray-500 mt-1">
                    {loading ? 'Loading available vehicles...' : 'No vehicles available'}
                  </p>
                )}
            </div>

            {/* Alert & Route Configuration */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <AlertCircle className="w-5 h-5 mr-2" />
                  {t('alert_route_config')}
                </h3>
                <button
                  type="button"
                  onClick={() => setShowAlertCreation(true)}
                  className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 flex items-center space-x-1"
                >
                  <AlertCircle className="w-3 h-3" />
                  <span>{t('create_alert')}</span>
                </button>
              </div>
              
              {/* Alert Selection */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Mission Alerts (Optional)
                    <span className="text-xs text-blue-600 ml-2">
                      (Choose alerts that will monitor this mission)
                    </span>
                  </label>
                  
                  {/* No mission-alert configuration backend exists yet — honest empty state */}
                  <div className="border border-gray-200 rounded-lg p-4 text-center text-sm text-gray-500 bg-gray-50">
                    No reusable mission alerts have been configured yet.
                    Use "Create Alert" above to define alerts for this mission.
                  </div>
                </div>

              </div>

              {/* Manual Route Information */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Manual Route Information (Optional)
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={formData.origin_address}
                    onChange={(e) => handleInputChange('origin_address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Origin address..."
                  />
                  <input
                    type="text"
                    value={formData.destination_address}
                    onChange={(e) => handleInputChange('destination_address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Destination address..."
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Use only if specific addresses needed. Alerts already include geofences and corridors.
                </p>
              </div>
            </div>

            {/* Mission Zones: geofences + corridors linked to this mission */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                Mission Zones (Geofences & Corridors)
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Geofences ({selectedGeofenceIds.length} selected)
                  </label>
                  <div className="space-y-1 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-white">
                    {geofences
                      .filter(g => !selectedCustomer || g.customer_id == null || String(g.customer_id) === String(selectedCustomer.id))
                      .map(g => (
                        <label key={g.id} className="flex items-center space-x-2 text-sm hover:bg-gray-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={selectedGeofenceIds.includes(g.id)}
                            onChange={(e) => setSelectedGeofenceIds(prev =>
                              e.target.checked ? [...prev, g.id] : prev.filter(id => id !== g.id)
                            )}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="flex-1 truncate">{g.name} <span className="text-xs text-gray-400">({g.type})</span></span>
                        </label>
                      ))}
                    {geofences.length === 0 && (
                      <p className="text-xs text-gray-500 p-1">No geofences defined yet.</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Corridors ({selectedCorridorIds.length} selected)
                  </label>
                  <div className="space-y-1 max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-white">
                    {corridors
                      .filter(c => !selectedCustomer || c.customer_id == null || String(c.customer_id) === String(selectedCustomer.id))
                      .map(c => (
                        <label key={c.id} className="flex items-center space-x-2 text-sm hover:bg-gray-50 p-1 rounded">
                          <input
                            type="checkbox"
                            checked={selectedCorridorIds.includes(c.id)}
                            onChange={(e) => setSelectedCorridorIds(prev =>
                              e.target.checked ? [...prev, c.id] : prev.filter(id => id !== c.id)
                            )}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="flex-1 truncate">{c.name}</span>
                        </label>
                      ))}
                    {corridors.length === 0 && (
                      <p className="text-xs text-gray-500 p-1">No corridors defined yet.</p>
                    )}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Zones linked to the mission are monitored by the alert engine during execution.
              </p>
            </div>

            {/* Timing */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                {t('schedule')}
              </h3>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('planned_start_time')}
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.planned_start_time}
                    onChange={(e) => {
                      const start = e.target.value
                      setFormData(current => {
                        if (!start || current.planned_end_time) return { ...current, planned_start_time: start }
                        const end = new Date(new Date(start).getTime() + defaultDurationMinutes * 60000)
                        const localEnd = new Date(end.getTime() - end.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
                        return { ...current, planned_start_time: start, planned_end_time: localEnd }
                      })
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('planned_end_time')}
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.planned_end_time}
                    onChange={(e) => handleInputChange('planned_end_time', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                <Users className="w-4 h-4 mr-2" />
                {t('customer_information')}
              </h3>
              
              {Array.isArray(customers) && customers.length > 0 ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('select_customer')}
                  </label>
                  <select
                    value={formData.customer_id}
                    onChange={(e) => handleInputChange('customer_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a customer...</option>
                    {Array.isArray(customers) && customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.company_name} ({customer.customer_code})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer Name (Manual Entry)
                  </label>
                  <input
                    type="text"
                    value={formData.customer_id}
                    onChange={(e) => handleInputChange('customer_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Customer name..."
                  />
                </div>
              )}
            </div>


            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                disabled={loading || !formData.mission_name || !formData.primary_staff_id}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>{editingMission ? t('updating') : t('creating')}</span>
                    </>
                  ) : (
                    <>
                      <Package className="w-4 h-4" />
                      <span>{editingMission ? t('update_mission') : t('create_mission')}</span>
                    </>
                  )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Surveillance rule editor, pre-filled with the mission's zones */}
      {showAlertCreation && (
        <AlertRuleEditor
          isOpen={showAlertCreation}
          onClose={() => setShowAlertCreation(false)}
          onSaved={() => setShowAlertCreation(false)}
          presetGeofenceIds={selectedGeofenceIds}
          presetCorridorIds={selectedCorridorIds}
          showMessage={showMessage}
        />
      )}
    </div>
  )
}

export default MissionCreator
